/**
 * Discord webhook sharing utilities
 * Generates branded share images and posts to Discord webhooks
 */

export interface DiscordShareOptions {
  webhookUrl: string;
  username: string;
  snapId?: string;
  snapImageUrl?: string;
  metricName?: string;
  message?: string;
  textContent?: string;
}

/**
 * Posts a snap share to Discord webhook
 * Sends a message with the snap link and optionally attaches the snap image
 */
export async function shareToDiscord(options: DiscordShareOptions): Promise<void> {
  const { webhookUrl, username, snapId, snapImageUrl, metricName, message, textContent } = options;
  
  console.log('[Discord Share] Starting Discord share with options:', {
    webhookUrl: webhookUrl ? `${webhookUrl.substring(0, 50)}...` : 'MISSING',
    username,
    snapId,
    snapImageUrl: snapImageUrl ? 'Present' : 'None',
    metricName: metricName || 'None',
    textContent: textContent ? 'Present' : 'None'
  });
  
  try {
    // Validate webhook URL (accept both discord.com and discordapp.com)
    if (!webhookUrl || (!webhookUrl.includes('discord.com') && !webhookUrl.includes('discordapp.com'))) {
      throw new Error('Invalid Discord webhook URL');
    }
    
    // Build the share URL (if snap ID is available)
    const shareUrl = snapId ? `${window.location.origin}/snap/${snapId}` : `${window.location.origin}`;
    console.log('[Discord Share] Share URL:', shareUrl);
    
    // Build the Discord message
    const defaultMessage = `${username} posted a new progress snap on gitfit.ai!`;
    const displayMessage = message || defaultMessage;
    console.log('[Discord Share] Message:', displayMessage);
    
    // Create embed with branding
    const embed = {
      title: "🏋️ New Progress Snap",
      description: `**${username}** posted their progress!\n\n[Join gitfit.ai for free!](${window.location.origin})`,
      url: snapId ? shareUrl : undefined, // Only include URL if we have a snap ID
      color: 0x6366f1, // Indigo color matching gitfit.ai branding
      fields: [] as Array<{ name: string; value: string; inline?: boolean }>,
      footer: {
        text: "gitfit.ai - Track. Share. Achieve.",
        icon_url: `${window.location.origin}/gitfitsmol.png`
      },
      timestamp: new Date().toISOString(),
    };

    // Add metric info if provided
    if (metricName) {
      embed.fields.push({
        name: "📊 Metric",
        value: metricName,
        inline: true
      });
    }

    // Add text content if provided
    if (textContent && textContent.trim()) {
      embed.fields.push({
        name: "💬 Note",
        value: textContent.trim()
      });
    }

    // Add image if provided
    if (snapImageUrl) {
      embed['image'] = {
        url: snapImageUrl
      };
    }

    // Add app store badge in thumbnail
    embed['thumbnail'] = {
      url: `${window.location.origin}/brand-logo-nobg.png`
    };

    const discordPayload = {
      content: displayMessage,
      embeds: [embed],
      // Add username as the webhook display name
      username: "gitfit.ai"
    };

    console.log('[Discord Share] Sending payload to Discord:', JSON.stringify(discordPayload, null, 2));

    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(discordPayload),
    });

    console.log('[Discord Share] Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[Discord Share] Error response:', errorText);
      throw new Error(`Discord webhook failed: ${response.status} ${errorText}`);
    }

    console.log("[Discord Share] ✅ Successfully posted to Discord!");
  } catch (error) {
    console.error("[Discord Share] ❌ Failed to post to Discord:", error);
    if (error instanceof Error) {
      console.error('[Discord Share] Error details:', error.message, error.stack);
    }
    throw error;
  }
}

/**
 * Creates a branded share image URL for Discord
 * Uses the snap's existing image and adds branding overlay information
 */
export function createBrandedShareUrl(snapImageUrl: string): string {
  // For now, return the snap image URL directly
  // In the future, we could implement server-side image processing to add branding overlay
  return snapImageUrl;
}
